import React, { useLayoutEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useDocumentPdf } from '@/hooks/useDocumentPdf';
import { getPdfPageWidth } from '@/lib/pdf-page-width';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, Download, RefreshCw, AlertCircle } from 'lucide-react';

// Set up the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfDocumentViewerProps {
  messageId: string;
  userId?: string | null;
  title?: string;
  onDownload?: () => void;
  onDocumentLoad?: (documentInfo: { title: string; fileSize?: number }) => void;
  /**
   * When true, downloads/copy/right-click are blocked and the viewer fires
   * onExportBlocked instead of triggering the actual download.
   */
  exportBlocked?: boolean;
  onExportBlocked?: () => void;
}

export const PdfDocumentViewer: React.FC<PdfDocumentViewerProps> = ({
  messageId,
  userId,
  title = 'Document',
  onDownload,
  onDocumentLoad,
  exportBlocked = false,
  onExportBlocked,
}) => {
  const { documentPdf, loading, generating, waitingForDocument, error, refetch, triggerGeneration } = useDocumentPdf(messageId, userId);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(0);
  const generationPending = generating || (!!userId && waitingForDocument);
  const pdfUrl = documentPdf?.pdfUrl;

  useLayoutEffect(() => {
    const container = pdfContentRef.current;
    if (!container || !pdfUrl) return;

    const updateWidth = () => {
      setPageWidth(getPdfPageWidth(container.clientWidth));
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, [pdfUrl, loading, generationPending, error]);

  // Notify parent when document loads
  React.useEffect(() => {
    if (documentPdf?.messageId === messageId && onDocumentLoad) {
      onDocumentLoad({
        title: documentPdf.title || title,
        fileSize: documentPdf.fileSize || undefined
      });
    }
  }, [documentPdf, messageId, title, onDocumentLoad]);

  const handleDownload = () => {
    if (exportBlocked) {
      onExportBlocked?.();
      return;
    }
    if (documentPdf?.pdfUrl) {
      const link = document.createElement('a');
      link.href = documentPdf.pdfUrl;
      // Use document title for filename, fallback to provided title
      const filename = `${documentPdf.title || title}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onDownload?.();
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF. Try refreshing.');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-muted/20 rounded-lg border border-border/50">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Loading document...</p>
      </div>
    );
  }

  // Generating state
  if (generationPending) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-muted/20 rounded-lg border border-border/50">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Generating Document...</p>
        <p className="text-xs text-muted-foreground mt-2">This may take a moment</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-muted/20 rounded-lg border border-destructive/50">
        <AlertCircle className="h-8 w-8 text-destructive mb-4" />
        <p className="text-sm text-destructive mb-4">{error}</p>
        <Button variant="outline" size="sm" onClick={refetch}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  // No PDF available state - offer to generate
  if (!documentPdf) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-muted/20 rounded-lg border border-border/50">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground mb-4">PDF document not available</p>
        {userId ? (
          <Button variant="outline" size="sm" onClick={triggerGeneration}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate PDF
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Sign in to generate PDF</p>
        )}
      </div>
    );
  }

  // PDF viewer
  return (
    <div
      className="flex h-full min-w-0 flex-col overflow-x-hidden"
      onContextMenu={(e) => {
        if (exportBlocked) {
          e.preventDefault();
          onExportBlocked?.();
        }
      }}
      style={exportBlocked ? { userSelect: 'none', WebkitUserSelect: 'none' } : undefined}
    >
      {/* PDF Content */}
      <div ref={pdfContentRef} className="flex min-w-0 flex-1 flex-col items-center overflow-x-hidden bg-muted/10 py-4">
        {pdfError ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
            <AlertCircle className="h-8 w-8 text-destructive mb-4" />
            <p className="text-sm text-destructive mb-4">{pdfError}</p>
            <Button variant="outline" size="sm" onClick={refetch}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        ) : (
          <Document
            file={documentPdf.pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center h-[500px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            }
          >
            {pageWidth > 0 && Array.from({ length: numPages }, (_, index) => (
              <Page 
                key={`page_${index + 1}`}
                pageNumber={index + 1} 
                width={pageWidth}
                className="mb-4 max-w-full shadow-lg"
                renderTextLayer={!exportBlocked}
                renderAnnotationLayer={true}
              />
            ))}
          </Document>
        )}
      </div>

      {/* Page count indicator */}
      {numPages > 0 && (
        <div className="flex items-center justify-center px-4 py-2 border-t border-border bg-muted/30">
          <span className="text-sm text-muted-foreground">
            {numPages} {numPages === 1 ? 'page' : 'pages'}
          </span>
        </div>
      )}
    </div>
  );
};
